import React from "react";
import MuiGrid from "@mui/material/Grid";
import type { GridProps as MuiGridProps } from "@mui/material/Grid";
import type { BaseAtomProps } from "@/components/types";

export interface GridProps
  extends Omit<MuiGridProps, "container" | "item">,
    BaseAtomProps {
  /**
   * Grid type - container (Row) or item (Col)
   */
  container?: boolean;

  /**
   * Grid item size (1-12) with responsive breakpoints
   */
  size?:
    | number
    | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

  /**
   * Grid spacing for container
   */
  spacing?:
    | number
    | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

  /**
   * Flex direction for container
   */
  direction?: "row" | "column" | "row-reverse" | "column-reverse";

  /**
   * Justify content alignment
   */
  justifyContent?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";

  /**
   * Align items alignment
   */
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch" | "baseline";

  /**
   * Mobile-first gutter size
   */
  gutter?: "none" | "small" | "medium" | "large";
}

const GridComponent: React.FC<GridProps> = ({
  container = false,
  size,
  spacing,
  direction = "row",
  justifyContent = "flex-start",
  alignItems = "flex-start",
  gutter = "medium",
  className,
  "data-testid": dataTestId,
  children,
  ...props
}) => {
  // Convert gutter to spacing
  const gutterToSpacing = {
    none: 0,
    small: { xs: 1, md: 2 },
    medium: { xs: 2, md: 3 },
    large: { xs: 3, md: 4 },
  };

  const effectiveSpacing =
    spacing || (container ? gutterToSpacing[gutter] : undefined);

  return (
    <MuiGrid
      container={container}
      size={!container && typeof size === "number" ? size : undefined}
      spacing={container ? effectiveSpacing : undefined}
      className={className}
      data-testid={dataTestId}
      sx={{
        // Container-specific styles
        ...(container && {
          flexDirection: direction,
          justifyContent,
          alignItems,
        }),
        // Mobile-first responsive behavior
        flexWrap: container ? "wrap" : undefined,
      }}
      {...props}
    >
      {children}
    </MuiGrid>
  );
};

// Apply memoization with React.memo
export const Grid = React.memo(GridComponent);

// Set display name for debugging
(Grid as any).displayName = "Grid";

// Row component (Grid container)
export const Row: React.FC<Omit<GridProps, "container">> = (props) => (
  <Grid container {...props} />
);

// Col component (Grid item)
export const Col: React.FC<Omit<GridProps, "container">> = (props) => (
  <Grid {...props} />
);

// Set display names
(Row as any).displayName = "Row";
(Col as any).displayName = "Col";
